import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Navigation from '../components/Navigation.jsx';
import Footer from '../components/Footer.jsx';
import {
    Container,
    Text,
    Heading,
    Button,
    Icon
} from '../components/ui/index.jsx';

export default function TermsOfService() {
    const navigate = useNavigate();
    const location = useLocation();
    const lastUpdated = 'December 30, 2025';

    // Scroll to hash section (e.g., #refund)
    useEffect(() => {
        if (location.hash) {
            const element = document.getElementById(location.hash.slice(1));
            if (element) {
                setTimeout(() => {
                    element.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }
        }
    }, [location.hash]);

    return (
        <div className="min-h-screen text-white selection:bg-[#7B61FF] selection:text-white relative overflow-hidden">
            {/* Aurora Background */}
            <div className="fixed inset-0 aurora-bg z-0 pointer-events-none"></div>

            {/* Ambient Orbs */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#7B61FF]/20 rounded-full blur-[100px] animate-pulse-slow"></div>
                <div className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-[#9F7AEA]/20 rounded-full blur-[100px] animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
            </div>

            {/* Content Wrapper */}
            <div className="relative z-10 flex flex-col min-h-screen">
                <Navigation />

                <main className="flex-1">
                    <Container size="4xl" className="py-12">
                        {/* Header */}
                        <div className="text-center mb-12 px-4">
                            <Heading level={1} className="mb-4 bg-clip-text text-transparent bg-gradient-to-b from-white to-white/50">
                                Terms of Service
                            </Heading>
                            <Text variant="secondary" size="sm">
                                Last updated: {lastUpdated}
                            </Text>
                            <div className="w-24 h-1 bg-[#7B61FF] mx-auto rounded-full shadow-[0_0_10px_#7B61FF] mt-6"></div>
                        </div>

                        {/* Content */}
                        <div className="glass-panel p-8 md:p-12 rounded-2xl border-white/10 space-y-8">

                            {/* Introduction */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">1. Introduction</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    Welcome to TheArchitect ("we," "our," or "us"). By accessing or using our AI-powered
                                    software architecture platform at theaiarchitect.ai (the "Service"), you agree to be bound
                                    by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not
                                    use our Service.
                                </Text>
                            </section>

                            {/* Service Description */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">2. Service Description</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    The Architect is an AI-powered platform that transforms ideas into software architecture,
                                    including data models, tech stack recommendations, and development prompts. Our Service
                                    uses artificial intelligence (Claude by Anthropic) to generate content.
                                </Text>
                            </section>

                            {/* Eligibility */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">3. Eligibility</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    You must be at least 18 years old to use our Service. By using the Service, you represent
                                    and warrant that you meet this age requirement and have the legal capacity to enter into
                                    these Terms.
                                </Text>
                            </section>

                            {/* Account Registration */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">4. Account Registration</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    To access certain features, you must create an account using Google OAuth. You are
                                    responsible for maintaining the confidentiality of your account and for all activities
                                    that occur under your account. You agree to notify us immediately of any unauthorized
                                    use of your account.
                                </Text>
                            </section>

                            {/* AI-Generated Content Disclaimer */}
                            <section className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6">
                                <Heading level={3} className="mb-4 text-yellow-400">5. AI-Generated Content Disclaimer</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">IMPORTANT:</strong> All content generated by our Service
                                    is produced using artificial intelligence and is provided "AS-IS" without any warranties.
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
                                    <li>AI outputs may contain inaccuracies, errors, or "hallucinations"</li>
                                    <li>Generated code may contain bugs, security vulnerabilities, or logical errors</li>
                                    <li>We do not guarantee the accuracy, completeness, or fitness for any particular purpose</li>
                                    <li>You must review, validate, and test all AI-generated content before use</li>
                                    <li>Professional review is recommended before deploying code to production</li>
                                    <li>You bear full responsibility for any code you deploy or use</li>
                                </ul>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">NOT PROFESSIONAL ADVICE:</strong> AI outputs are not a substitute
                                    for professional legal, medical, financial, or other expert advice. Do not rely on AI-generated
                                    content for healthcare decisions, legal matters, financial planning, or any high-stakes decisions.
                                </Text>
                                <Text variant="secondary" className="leading-relaxed">
                                    <strong className="text-white">AI TRAINING:</strong> We do not use your input prompts or the
                                    AI-generated outputs to train or improve AI models. Your data remains your data.
                                </Text>
                            </section>

                            {/* Acceptable Use */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">6. Acceptable Use Policy</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    You agree not to use our Service for any unlawful purpose or in violation of these Terms.
                                    Prohibited uses include, but are not limited to:
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300">
                                    <li>Generating malware, viruses, or malicious code</li>
                                    <li>Creating content that promotes violence, hate speech, or discrimination</li>
                                    <li>Attempting to bypass security measures or access restrictions</li>
                                    <li>Generating content that infringes on intellectual property rights</li>
                                    <li>Using the Service for illegal activities or fraud</li>
                                    <li>Generating content related to weapons or explosives</li>
                                    <li>Creating content involving minors in any harmful context</li>
                                    <li>Automated access or scraping without permission</li>
                                </ul>
                            </section>

                            {/* Intellectual Property */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">7. Intellectual Property & AI Output Ownership</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Your Content:</strong> You retain ownership of the ideas
                                    and prompts you submit to the Service. You grant us a limited, non-exclusive license to process
                                    your inputs solely to provide the Service. We do not claim ownership of your inputs.
                                </Text>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">AI Output Ownership:</strong> Subject to your compliance with
                                    these Terms, TheArchitect assigns to you all rights, title, and interest in the AI-generated
                                    outputs created from your prompts. You may use these outputs for personal and commercial purposes.
                                    However:
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
                                    <li>We make no representations regarding the originality or non-infringement of AI outputs</li>
                                    <li>Similar outputs may be generated for other users with similar prompts</li>
                                    <li>AI outputs are not guaranteed to be unique or proprietary</li>
                                    <li>You are responsible for ensuring outputs don't infringe third-party rights before use</li>
                                </ul>
                                <Text variant="secondary" className="leading-relaxed">
                                    <strong className="text-white">Our Service:</strong> TheArchitect platform, including
                                    its design, features, technology, and branding, remains our exclusive property and is protected
                                    by intellectual property laws.
                                </Text>
                            </section>

                            {/* Subscription and Payments */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">8. Subscription and Payments</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Credit System:</strong> Our Service operates on a credit-based
                                    system with two types of credits:
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
                                    <li><strong className="text-white">Subscription Credits:</strong> Included with your subscription plan.
                                    These credits reset each billing period and expire if you cancel your subscription (at the end of your paid period).</li>
                                    <li><strong className="text-white">Purchased Credits:</strong> One-time credit purchases that never expire
                                    and remain in your account regardless of subscription status.</li>
                                </ul>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    Credits are consumed when generating AI content.
                                </Text>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Subscriptions:</strong> Paid subscriptions automatically renew
                                    at the end of each billing period unless cancelled. You will be charged the then-current
                                    subscription fee.
                                </Text>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Cancellation:</strong> You may cancel your subscription at
                                    any time through your account settings. Cancellation takes effect at the end of the current
                                    billing period. You retain full access and credits until the end of your paid period.
                                    Subscription credits expire at period end; purchased credits remain in your account.
                                </Text>
                                <Text variant="secondary" className="leading-relaxed">
                                    <strong className="text-white">Price Changes:</strong> We may change subscription prices
                                    with at least 30 days' notice. Continued use after a price change constitutes acceptance.
                                </Text>
                            </section>

                            {/* Refund Policy */}
                            <section id="refund" className="scroll-mt-24">
                                <Heading level={3} className="mb-4 text-[#BA87FF]">9. Refund Policy</Heading>

                                <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4 mb-6">
                                    <Text variant="secondary" className="leading-relaxed">
                                        <strong className="text-orange-400">All Sales Final:</strong> All purchases are final.
                                        No refunds will be issued for subscriptions or credit purchases.
                                    </Text>
                                </div>

                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Cancellation:</strong> When you cancel your subscription,
                                    you retain full access and credits until the end of your current billing period.
                                    Subscription credits expire at period end; purchased credits remain in your account permanently.
                                </Text>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Exceptions (handled case-by-case):</strong>
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300 mb-4">
                                    <li>Technical issues preventing service use</li>
                                    <li>Billing errors or duplicate charges</li>
                                </ul>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Billing Issues:</strong> Contact us at{' '}
                                    <a href="mailto:support@theaiarchitect.ai" className="text-[#7B61FF] hover:underline">
                                        support@theaiarchitect.ai
                                    </a>
                                    {' '}for billing issues. We respond within 24 hours.
                                </Text>
                                <Text variant="secondary" className="leading-relaxed text-sm italic">
                                    Payments processed by Paddle.com (Merchant of Record).
                                </Text>
                            </section>

                            {/* Limitation of Liability */}
                            <section className="bg-red-500/10 border border-red-500/30 rounded-lg p-6">
                                <Heading level={3} className="mb-4 text-red-400">10. Limitation of Liability</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    TO THE MAXIMUM EXTENT PERMITTED BY LAW:
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300">
                                    <li>THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND</li>
                                    <li>WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE</li>
                                    <li>WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES</li>
                                    <li>OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE PAST 12 MONTHS</li>
                                    <li>WE ARE NOT LIABLE FOR ANY DAMAGES RESULTING FROM AI-GENERATED CONTENT</li>
                                </ul>
                            </section>

                            {/* Indemnification */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">11. Indemnification</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    You agree to indemnify and hold harmless TheArchitect, its officers, directors, employees,
                                    and agents from any claims, damages, losses, or expenses arising from your use of the Service,
                                    your violation of these Terms, or your use of AI-generated content.
                                </Text>
                            </section>

                            {/* Termination */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">12. Termination</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    We may suspend or terminate your access to the Service at any time, with or without cause,
                                    and with or without notice. Upon termination, your right to use the Service will immediately
                                    cease. Provisions that by their nature should survive termination shall survive.
                                </Text>
                            </section>

                            {/* Dispute Resolution & Arbitration */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">13. Dispute Resolution & Arbitration</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Informal Resolution:</strong> Before filing any formal dispute,
                                    you agree to contact us through our contact page to attempt informal resolution. We will attempt
                                    to resolve disputes within 30 days.
                                </Text>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Binding Arbitration:</strong> If informal resolution fails,
                                    any dispute arising from these Terms or the Service shall be resolved through binding arbitration
                                    administered by the American Arbitration Association (AAA) under its Commercial Arbitration Rules.
                                    Arbitration shall take place in Delaware, United States, or may be conducted remotely.
                                </Text>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    <strong className="text-white">Opt-Out:</strong> You may opt out of arbitration by sending written
                                    notice to us within 30 days of first agreeing to these Terms.
                                </Text>
                                <Text variant="secondary" className="leading-relaxed">
                                    <strong className="text-white">Small Claims:</strong> Either party may bring claims in small claims
                                    court if eligible.
                                </Text>
                            </section>

                            {/* Class Action Waiver */}
                            <section className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-6">
                                <Heading level={3} className="mb-4 text-yellow-400">14. Class Action Waiver</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    YOU AND THEARCHITECT AGREE THAT EACH MAY BRING CLAIMS AGAINST THE OTHER ONLY IN YOUR OR ITS
                                    INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE
                                    ACTION. Unless both parties agree otherwise, the arbitrator may not consolidate more than one
                                    person's claims and may not preside over any form of representative or class proceeding.
                                </Text>
                            </section>

                            {/* EU Consumer Rights */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">15. EU Consumer Rights</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    If you are a consumer in the European Union, you have certain additional rights:
                                </Text>
                                <ul className="list-disc list-inside space-y-2 text-gray-300">
                                    <li><strong className="text-white">Right of Withdrawal:</strong> For digital content, you may have
                                    a 14-day withdrawal period. By using our Service immediately, you acknowledge that you waive this
                                    right once the service begins.</li>
                                    <li><strong className="text-white">Consumer Guarantees:</strong> Nothing in these Terms affects your
                                    statutory rights as a consumer under applicable EU law.</li>
                                    <li><strong className="text-white">Local Courts:</strong> Nothing in these Terms prevents you from
                                    bringing proceedings in the courts of your country of residence.</li>
                                </ul>
                            </section>

                            {/* Governing Law */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">16. Governing Law</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    These Terms shall be governed by and construed in accordance with the laws of the State of
                                    Delaware, United States, without regard to its conflict of law provisions. For EU users, mandatory
                                    consumer protection laws of your country of residence shall also apply where required.
                                </Text>
                            </section>

                            {/* Severability */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">17. Severability</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    If any provision of these Terms is found to be unenforceable or invalid by a court of competent
                                    jurisdiction, that provision shall be limited or eliminated to the minimum extent necessary, and
                                    the remaining provisions shall remain in full force and effect.
                                </Text>
                            </section>

                            {/* Force Majeure */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">18. Force Majeure</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    We shall not be liable for any failure or delay in performing our obligations where such failure
                                    or delay results from circumstances beyond our reasonable control, including but not limited to
                                    natural disasters, war, terrorism, riots, government actions, power failures, internet outages,
                                    or third-party service provider failures.
                                </Text>
                            </section>

                            {/* Entire Agreement */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">19. Entire Agreement</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    These Terms, together with our Privacy Policy and Cookie Policy, constitute the entire agreement
                                    between you and The Architect regarding the Service and supersede all prior agreements and
                                    understandings. No waiver of any term shall be deemed a further or continuing waiver.
                                </Text>
                            </section>

                            {/* Changes to Terms */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">20. Changes to Terms</Heading>
                                <Text variant="secondary" className="leading-relaxed">
                                    We reserve the right to modify these Terms at any time. We will notify you of material
                                    changes by posting the updated Terms on our website, updating the "Last updated" date, and
                                    where appropriate, sending you an email notification. Your continued use of the Service after
                                    such changes constitutes acceptance of the new Terms.
                                </Text>
                            </section>

                            {/* Contact */}
                            <section>
                                <Heading level={3} className="mb-4 text-[#BA87FF]">21. Contact Us</Heading>
                                <Text variant="secondary" className="leading-relaxed mb-4">
                                    If you have any questions about these Terms of Service, please contact us through our contact page:
                                </Text>
                                <Button
                                    onClick={() => navigate('/contact')}
                                    variant="ghost"
                                    className="text-[#7B61FF] hover:text-[#8A6DFF] underline p-0"
                                >
                                    Contact Us
                                </Button>
                            </section>

                            {/* Back Button */}
                            <div className="pt-8 border-t border-white/10 text-center">
                                <Button
                                    onClick={() => navigate(-1)}
                                    variant="ghost"
                                    className="flex items-center mx-auto"
                                >
                                    <Icon name="arrowLeft" size="xs" className="mr-2" />
                                    Go Back
                                </Button>
                            </div>
                        </div>
                    </Container>
                </main>

                <Footer />
            </div>
        </div>
    );
}
